import type { UUID } from '@/types/domain';

/**
 * Every TanStack Query key in one place.
 *
 * Keys are hierarchical, so a mutation can invalidate a whole resource with
 * `queryKey: keys.students.all` without knowing which filter combinations happen
 * to be cached. Scattering key literals across components is what makes caches
 * go stale in ways nobody can reproduce.
 */
export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },
  schools: {
    all: ['schools'] as const,
    list: (params: unknown) => ['schools', 'list', params] as const,
    picker: ['schools', 'picker'] as const,
    detail: (id: UUID) => ['schools', 'detail', id] as const,
  },
  campuses: {
    all: ['campuses'] as const,
    list: (params: unknown) => ['campuses', 'list', params] as const,
    picker: ['campuses', 'picker'] as const,
    detail: (id: UUID) => ['campuses', 'detail', id] as const,
  },
  academicYears: {
    all: ['academic-years'] as const,
    list: (params: unknown) => ['academic-years', 'list', params] as const,
    picker: ['academic-years', 'picker'] as const,
    current: ['academic-years', 'current'] as const,
  },
  grades: {
    all: ['grades'] as const,
    list: (params: unknown) => ['grades', 'list', params] as const,
    picker: ['grades', 'picker'] as const,
    detail: (id: UUID) => ['grades', 'detail', id] as const,
    subjects: (id: UUID) => ['grades', id, 'subjects'] as const,
  },
  gradeOfferings: {
    all: ['grade-offerings'] as const,
    list: (params: unknown) => ['grade-offerings', 'list', params] as const,
    detail: (id: UUID) => ['grade-offerings', 'detail', id] as const,
  },
  sections: {
    all: ['sections'] as const,
    list: (params: unknown) => ['sections', 'list', params] as const,
  },
  subjects: {
    all: ['subjects'] as const,
    list: (params: unknown) => ['subjects', 'list', params] as const,
    picker: ['subjects', 'picker'] as const,
  },
  students: {
    all: ['students'] as const,
    list: (params: unknown) => ['students', 'list', params] as const,
    detail: (id: UUID) => ['students', 'detail', id] as const,
    enrollments: (id: UUID) => ['students', id, 'enrollments'] as const,
    statusHistory: (id: UUID) => ['students', id, 'status-history'] as const,
    account: (id: UUID) => ['students', id, 'account'] as const,
  },
  enrollments: {
    all: ['enrollments'] as const,
    list: (params: unknown) => ['enrollments', 'list', params] as const,
  },
  guardians: {
    all: ['guardians'] as const,
    list: (params: unknown) => ['guardians', 'list', params] as const,
    detail: (id: UUID) => ['guardians', 'detail', id] as const,
    students: (id: UUID) => ['guardians', id, 'students'] as const,
    account: (id: UUID) => ['guardians', id, 'account'] as const,
  },
  studentProfile: {
    all: ['student-profile'] as const,
    guardians: (studentId: UUID) => ['student-profile', studentId, 'guardians'] as const,
    identifications: (studentId: UUID) => ['student-profile', studentId, 'identifications'] as const,
    addresses: (studentId: UUID) => ['student-profile', studentId, 'addresses'] as const,
    documents: (studentId: UUID, status?: unknown) =>
      ['student-profile', studentId, 'documents', status] as const,
    academicHistory: (studentId: UUID) => ['student-profile', studentId, 'academic-history'] as const,
  },
  employees: {
    all: ['employees'] as const,
    list: (params: unknown) => ['employees', 'list', params] as const,
    detail: (id: UUID) => ['employees', 'detail', id] as const,
  },
  teachers: {
    all: ['teachers'] as const,
    list: (params: unknown) => ['teachers', 'list', params] as const,
    detail: (id: UUID) => ['teachers', 'detail', id] as const,
  },
  academicSubjects: {
    all: ['academic-subjects'] as const,
    forOffering: (gradeOfferingId: UUID, status?: unknown) =>
      ['academic-subjects', gradeOfferingId, status] as const,
  },
  periods: {
    all: ['periods'] as const,
    forCampus: (campusId: UUID) => ['periods', campusId] as const,
  },
  calendar: {
    all: ['calendar'] as const,
    range: (params: unknown) => ['calendar', 'range', params] as const,
    forYear: (academicYearId: UUID) => ['calendar', 'year', academicYearId] as const,
  },
  teacherAssignments: {
    all: ['teacher-assignments'] as const,
    list: (params: unknown) => ['teacher-assignments', 'list', params] as const,
    detail: (id: UUID) => ['teacher-assignments', 'detail', id] as const,
    classTeachers: (sectionId: UUID) => ['teacher-assignments', 'class-teachers', sectionId] as const,
  },
  timetables: {
    all: ['timetables'] as const,
    forSection: (sectionId: UUID) => ['timetables', 'section', sectionId] as const,
    published: (sectionId: UUID) => ['timetables', 'published', sectionId] as const,
    detail: (id: UUID) => ['timetables', 'detail', id] as const,
    forTeacher: (teacherId: UUID) => ['timetables', 'teacher', teacherId] as const,
    mine: ['timetables', 'me'] as const,
  },
  attendance: {
    all: ['attendance'] as const,
    register: (params: unknown) => ['attendance', 'register', params] as const,
    forStudent: (studentId: UUID, from: string, to: string) =>
      ['attendance', 'student', studentId, from, to] as const,
    summary: (studentId: UUID, from: string, to: string) =>
      ['attendance', 'summary', studentId, from, to] as const,
    teacherDay: (date: string) => ['attendance', 'teacher-day', date] as const,
  },
  assignments: {
    all: ['assignments'] as const,
    list: (teacherAssignmentId: UUID, includeDrafts: boolean) =>
      ['assignments', 'list', teacherAssignmentId, includeDrafts] as const,
    detail: (id: UUID) => ['assignments', 'detail', id] as const,
    submissions: (id: UUID) => ['assignments', id, 'submissions'] as const,
  },
  examinations: {
    all: ['examinations'] as const,
    list: (params: unknown) => ['examinations', 'list', params] as const,
    detail: (id: UUID) => ['examinations', 'detail', id] as const,
    schedule: (id: UUID) => ['examinations', id, 'schedule'] as const,
    results: (id: UUID) => ['examinations', id, 'results'] as const,
    result: (id: UUID, enrollmentId: UUID) =>
      ['examinations', id, 'results', enrollmentId] as const,
  },
  examinationTypes: {
    all: ['examination-types'] as const,
  },
  gradingScales: {
    all: ['grading-scales'] as const,
    detail: (id: UUID) => ['grading-scales', 'detail', id] as const,
  },
  marks: {
    all: ['marks'] as const,
    sheet: (examinationSubjectId: UUID, sectionId: UUID) =>
      ['marks', examinationSubjectId, sectionId] as const,
  },
  academicTerms: {
    all: ['academic-terms'] as const,
    forYear: (academicYearId: UUID) => ['academic-terms', academicYearId] as const,
  },
  // ---- HR & payroll -------------------------------------------------------
  hr: {
    all: ['hr'] as const,
    settings: ['hr', 'settings'] as const,
    dashboard: ['hr', 'dashboard'] as const,
    payrollSummary: (periodId: UUID) => ['hr', 'report', 'payroll-summary', periodId] as const,
    leaveSummary: (params: unknown) => ['hr', 'report', 'leave-summary', params] as const,
  },
  leaveTypes: {
    all: ['leave-types'] as const,
    list: (activeOnly: boolean) => ['leave-types', 'list', activeOnly] as const,
    detail: (id: UUID) => ['leave-types', 'detail', id] as const,
  },
  leaveBalances: {
    all: ['leave-balances'] as const,
    mine: (year?: number) => ['leave-balances', 'me', year] as const,
    forEmployee: (employeeId: UUID, year?: number) =>
      ['leave-balances', employeeId, year] as const,
  },
  leaveRequests: {
    all: ['leave-requests'] as const,
    list: (params: unknown) => ['leave-requests', 'list', params] as const,
    mine: (params: unknown) => ['leave-requests', 'me', params] as const,
    awaitingMe: ['leave-requests', 'awaiting-me'] as const,
    onDate: (date?: string) => ['leave-requests', 'on-date', date] as const,
    detail: (id: UUID) => ['leave-requests', 'detail', id] as const,
  },
  salaryComponents: {
    all: ['salary-components'] as const,
    list: (activeOnly: boolean) => ['salary-components', 'list', activeOnly] as const,
  },
  salaryStructures: {
    all: ['salary-structures'] as const,
    list: (activeOnly: boolean) => ['salary-structures', 'list', activeOnly] as const,
    detail: (id: UUID) => ['salary-structures', 'detail', id] as const,
  },
  salaryAssignments: {
    all: ['salary-assignments'] as const,
    history: (employeeId: UUID) => ['salary-assignments', employeeId] as const,
    current: (employeeId: UUID) => ['salary-assignments', employeeId, 'current'] as const,
  },
  payroll: {
    all: ['payroll'] as const,
    list: (params: unknown) => ['payroll', 'list', params] as const,
    open: ['payroll', 'open'] as const,
    detail: (id: UUID) => ['payroll', 'detail', id] as const,
    adjustments: (periodId: UUID) => ['payroll', periodId, 'adjustments'] as const,
  },
  payslips: {
    all: ['payslips'] as const,
    forPeriod: (periodId: UUID, params: unknown) =>
      ['payslips', 'period', periodId, params] as const,
    forEmployee: (employeeId: UUID, params: unknown) =>
      ['payslips', 'employee', employeeId, params] as const,
    mine: (params: unknown) => ['payslips', 'me', params] as const,
    detail: (id: UUID) => ['payslips', 'detail', id] as const,
  },
  loans: {
    all: ['loans'] as const,
    list: (params: unknown) => ['loans', 'list', params] as const,
    forEmployee: (employeeId: UUID) => ['loans', 'employee', employeeId] as const,
    detail: (id: UUID) => ['loans', 'detail', id] as const,
  },

  finance: {
    all: ['finance'] as const,
    settings: ['finance', 'settings'] as const,
    dashboard: (params: unknown) => ['finance', 'dashboard', params] as const,
    events: (params: unknown) => ['finance', 'events', params] as const,
    report: (name: string, params: unknown) => ['finance', 'report', name, params] as const,
  },
  feeCategories: {
    all: ['fee-categories'] as const,
    list: (activeOnly: boolean) => ['fee-categories', 'list', activeOnly] as const,
  },
  feeStructures: {
    all: ['fee-structures'] as const,
    list: (params: unknown) => ['fee-structures', 'list', params] as const,
    detail: (id: UUID) => ['fee-structures', 'detail', id] as const,
  },
  feeAssignments: {
    all: ['fee-assignments'] as const,
    list: (params: unknown) => ['fee-assignments', 'list', params] as const,
    forEnrolment: (enrollmentId: UUID) => ['fee-assignments', 'enrolment', enrollmentId] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    list: (params: unknown) => ['invoices', 'list', params] as const,
    detail: (id: UUID) => ['invoices', 'detail', id] as const,
  },
  payments: {
    all: ['payments'] as const,
    list: (params: unknown) => ['payments', 'list', params] as const,
    detail: (id: UUID) => ['payments', 'detail', id] as const,
    receipt: (id: UUID) => ['payments', 'receipt', id] as const,
    balance: (studentId: UUID) => ['payments', 'balance', studentId] as const,
  },
  discounts: {
    all: ['discounts'] as const,
    list: (params: unknown) => ['discounts', 'list', params] as const,
  },
  scholarships: {
    all: ['scholarships'] as const,
    list: (params: unknown) => ['scholarships', 'list', params] as const,
  },
  lateFees: {
    all: ['late-fee-rules'] as const,
  },
  refunds: {
    all: ['refunds'] as const,
    list: (params: unknown) => ['refunds', 'list', params] as const,
    detail: (id: UUID) => ['refunds', 'detail', id] as const,
  },
  users: {
    all: ['users'] as const,
    list: (params: unknown) => ['users', 'list', params] as const,
    detail: (id: UUID) => ['users', 'detail', id] as const,
  },
  roles: {
    all: ['roles'] as const,
  },
  permissions: {
    all: ['permissions'] as const,
  },
  audit: {
    all: ['audit'] as const,
    list: (params: unknown) => ['audit', 'list', params] as const,
  },

  // ---- notifications & announcements --------------------------------------
  notifications: {
    all: ['notifications'] as const,
    feed: (params: unknown) => ['notifications', 'feed', params] as const,
    unreadCount: ['notifications', 'unread-count'] as const,
  },
  announcements: {
    all: ['announcements'] as const,
    feed: ['announcements', 'feed'] as const,
    list: (params: unknown) => ['announcements', 'list', params] as const,
    detail: (id: UUID) => ['announcements', 'detail', id] as const,
  },

  // ---- admissions ---------------------------------------------------------
  admissions: {
    all: ['admissions'] as const,
    list: (params: unknown) => ['admissions', 'list', params] as const,
    detail: (id: UUID) => ['admissions', 'detail', id] as const,
    documents: (id: UUID) => ['admissions', id, 'documents'] as const,
  },

  // ---- library ------------------------------------------------------------
  library: {
    all: ['library'] as const,
    list: ['library', 'list'] as const,
    detail: (id: UUID) => ['library', 'detail', id] as const,
    dashboard: (libraryId?: UUID) => ['library', 'dashboard', libraryId] as const,
    circulationDashboard: ['library', 'circulation-dashboard'] as const,
    locations: (libraryId?: UUID) => ['library', 'locations', libraryId] as const,
    report: (name: string, params: unknown) => ['library', 'report', name, params] as const,
  },
  books: {
    all: ['books'] as const,
    search: (params: unknown) => ['books', 'search', params] as const,
    detail: (id: UUID) => ['books', 'detail', id] as const,
    copies: (id: UUID) => ['books', 'copies', id] as const,
    languages: ['books', 'languages'] as const,
  },
  bookCopies: {
    all: ['book-copies'] as const,
    detail: (id: UUID) => ['book-copies', 'detail', id] as const,
    scan: (code: string) => ['book-copies', 'scan', code] as const,
  },
  catalogueReference: {
    categories: ['book-categories'] as const,
    authors: (params: unknown) => ['authors', params] as const,
    publishers: (params: unknown) => ['publishers', params] as const,
  },
  libraryMembers: {
    all: ['library-members'] as const,
    list: (params: unknown) => ['library-members', 'list', params] as const,
    mine: ['library-members', 'me'] as const,
    detail: (id: UUID) => ['library-members', 'detail', id] as const,
    eligibility: (id: UUID) => ['library-members', 'eligibility', id] as const,
  },
  circulation: {
    all: ['circulation'] as const,
    loans: (params: unknown) => ['circulation', 'loans', params] as const,
    overdue: (libraryId?: UUID) => ['circulation', 'overdue', libraryId] as const,
    loan: (id: UUID) => ['circulation', 'loan', id] as const,
    memberLoans: (memberId: UUID) => ['circulation', 'member-loans', memberId] as const,
  },
  reservations: {
    all: ['reservations'] as const,
    list: (params: unknown) => ['reservations', 'list', params] as const,
    mine: ['reservations', 'me'] as const,
    queue: (bookId: UUID) => ['reservations', 'queue', bookId] as const,
    detail: (id: UUID) => ['reservations', 'detail', id] as const,
  },
  libraryFines: {
    all: ['library-fines'] as const,
    list: (params: unknown) => ['library-fines', 'list', params] as const,
    mine: ['library-fines', 'me'] as const,
    forMember: (memberId: UUID) => ['library-fines', 'member', memberId] as const,
    detail: (id: UUID) => ['library-fines', 'detail', id] as const,
    lost: (params: unknown) => ['library-fines', 'lost', params] as const,
    damaged: (params: unknown) => ['library-fines', 'damaged', params] as const,
  },
  acquisitions: {
    all: ['acquisitions'] as const,
    list: (params: unknown) => ['acquisitions', 'list', params] as const,
    detail: (id: UUID) => ['acquisitions', 'detail', id] as const,
    suppliers: (activeOnly: boolean) => ['acquisitions', 'suppliers', activeOnly] as const,
  },
  libraryInventory: {
    all: ['library-inventory'] as const,
    summary: (libraryId?: UUID) => ['library-inventory', 'summary', libraryId] as const,
    audits: (params: unknown) => ['library-inventory', 'audits', params] as const,
    audit: (id: UUID) => ['library-inventory', 'audit', id] as const,
  },

  // ---- transport ----------------------------------------------------------
  transport: {
    all: ['transport'] as const,
    dashboard: ['transport', 'dashboard'] as const,
    settings: ['transport', 'settings'] as const,
    events: (params: unknown) => ['transport', 'events', params] as const,
    report: (name: string, params: unknown) => ['transport', 'report', name, params] as const,
  },
  vehicles: {
    all: ['vehicles'] as const,
    list: (params: unknown) => ['vehicles', 'list', params] as const,
    detail: (id: UUID) => ['vehicles', 'detail', id] as const,
    documents: (id: UUID) => ['vehicles', id, 'documents'] as const,
    schedules: (id: UUID) => ['vehicles', id, 'schedules'] as const,
    locations: ['vehicles', 'locations'] as const,
    location: (id: UUID) => ['vehicles', 'locations', id] as const,
  },
  transportDrivers: {
    all: ['transport-drivers'] as const,
    list: (params: unknown) => ['transport-drivers', 'list', params] as const,
    detail: (id: UUID) => ['transport-drivers', 'detail', id] as const,
    documents: (id: UUID) => ['transport-drivers', id, 'documents'] as const,
  },
  transportRoutes: {
    all: ['transport-routes'] as const,
    list: (params: unknown) => ['transport-routes', 'list', params] as const,
    detail: (id: UUID) => ['transport-routes', 'detail', id] as const,
    stops: (id: UUID) => ['transport-routes', id, 'stops'] as const,
  },
  transportEnrollments: {
    all: ['transport-enrollments'] as const,
    list: (params: unknown) => ['transport-enrollments', 'list', params] as const,
    detail: (id: UUID) => ['transport-enrollments', 'detail', id] as const,
    forStudent: (studentId: UUID) => ['transport-enrollments', 'student', studentId] as const,
  },
  trips: {
    all: ['trips'] as const,
    list: (params: unknown) => ['trips', 'list', params] as const,
    board: (date?: string) => ['trips', 'board', date] as const,
    detail: (id: UUID) => ['trips', 'detail', id] as const,
    roster: (id: UUID) => ['trips', id, 'roster'] as const,
    mine: (params: unknown) => ['trips', 'me', params] as const,
    myRoster: (tripId: UUID) => ['trips', 'me', tripId, 'roster'] as const,
  },
  transportIncidents: {
    all: ['transport-incidents'] as const,
    list: (params: unknown) => ['transport-incidents', 'list', params] as const,
    detail: (id: UUID) => ['transport-incidents', 'detail', id] as const,
  },
  maintenance: {
    all: ['maintenance'] as const,
    list: (params: unknown) => ['maintenance', 'list', params] as const,
    due: ['maintenance', 'due'] as const,
  },

  // ---- assets & stock -----------------------------------------------------
  assets: {
    all: ['assets'] as const,
    list: (params: unknown) => ['assets', 'list', params] as const,
    dashboard: ['assets', 'dashboard'] as const,
    detail: (id: UUID) => ['assets', 'detail', id] as const,
    history: (id: UUID) => ['assets', id, 'history'] as const,
  },
  assetReference: {
    all: ['asset-reference'] as const,
    categories: ['asset-reference', 'categories'] as const,
    locationsAll: ['asset-reference', 'locations'] as const,
    locations: (campusId?: UUID) => ['asset-reference', 'locations', campusId] as const,
  },
  assetAudits: {
    all: ['asset-audits'] as const,
    list: (params: unknown) => ['asset-audits', 'list', params] as const,
    detail: (id: UUID) => ['asset-audits', 'detail', id] as const,
    lines: (id: UUID) => ['asset-audits', id, 'lines'] as const,
  },
  stock: {
    all: ['stock-items'] as const,
    list: (params: unknown) => ['stock-items', 'list', params] as const,
    detail: (id: UUID) => ['stock-items', 'detail', id] as const,
    movements: (id: UUID, params: unknown) => ['stock-items', id, 'movements', params] as const,
  },

  // ---- student & parent portal -------------------------------------------
  /**
   * `subject` is the portal subject's key: 'me' for a student, or a child's
   * studentId for a guardian — so two children never share a cache entry.
   */
  portal: {
    all: ['portal'] as const,
    me: ['portal', 'me'] as const,
    children: ['portal', 'children'] as const,
    attendance: (subject: string, from?: string, to?: string) =>
      ['portal', subject, 'attendance', from ?? null, to ?? null] as const,
    timetable: (subject: string) => ['portal', subject, 'timetable'] as const,
    results: (subject: string) => ['portal', subject, 'results'] as const,
    result: (subject: string, examinationId: UUID) =>
      ['portal', subject, 'results', examinationId] as const,
    fees: (subject: string) => ['portal', subject, 'fees'] as const,
    receipt: (subject: string, paymentId: UUID) =>
      ['portal', subject, 'receipt', paymentId] as const,
    transport: (subject: string) => ['portal', subject, 'transport'] as const,
    library: (subject: string) => ['portal', subject, 'library'] as const,
    myAssignments: (params: unknown) => ['portal', 'assignments', 'mine', params] as const,
    childAssignments: (params: unknown) => ['portal', 'assignments', 'children', params] as const,
  },

  // ---- platform -----------------------------------------------------------
  imports: {
    all: ['imports'] as const,
    types: ['imports', 'types'] as const,
    history: (params: unknown) => ['imports', 'history', params] as const,
    detail: (id: UUID) => ['imports', 'detail', id] as const,
  },
  search: {
    /** `types` undefined or empty means every type the caller may see. */
    query: (q: string, types?: string[]) => ['search', q, types ?? []] as const,
  },
  insight: {
    all: ['insight'] as const,
    /** `campusId` undefined/null means the whole school. */
    summary: (campusId?: UUID | null) => ['insight', 'summary', campusId ?? null] as const,
  },
} as const;
